<script setup lang="ts">
import { computed, ref, watch, onBeforeMount } from "vue";
import { useI18n } from "vue-i18n";
import { RuleDimension } from "@/modules/rule_engine/ruleEngineClasses";
import { useRuleEnginedStore } from "@/modules/rule_engine/store";

const { t } = useI18n();

export type InSelection = {
	selectedValues: (string | number)[];
};

const props = defineProps<{
	type:
		| "in_number"
		| "in_string"
		| "not_in_number"
		| "not_in_string"
		| "day_of_week";
	selectedRuleDimension: RuleDimension;
}>();

const selection = defineModel<InSelection>();
const isInputValid = defineModel<boolean>("isInputValid", {
	default: () => ref(true),
});

const selectedValues = computed({
	get() {
		return selection.value.selectedValues ?? [];
	},
	set(newValues) {
		selection.value = {
			selectedValues: newValues,
		};
	},
});

watch(
	selectedValues,
	(newValue) => {
		isInputValid.value = newValue.length > 0;
	},
	{ deep: true }
);

onBeforeMount(() => {
	isInputValid.value = selectedValues.value.length > 0;
	if (props.type === "day_of_week") {
		loadDaysOfWeek();
	} else {
		loadPossibleValues();
	}
});

const possibleValues = ref<{ display_name: string; value: string | null }[]>(
	[]
);
const isLoadingPossibleValues = ref<boolean>(false);

/** Monday-first for the picker; `days_of_week` is keyed by JS day number. */
const DAY_OF_WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];

const loadDaysOfWeek = () => {
	possibleValues.value = DAY_OF_WEEK_ORDER.map((day) => ({
		display_name: t(`rule_engine.days_of_week.${day}`),
		value: String(day),
	}));
};

type GroupedOption = { display_name: string; values: (string | null)[] };

const groupedPossibleValues = computed<GroupedOption[]>(() => {
	const groups = new Map<string, (string | null)[]>();

	possibleValues.value.forEach(({ display_name, value }) => {
		groups.set(display_name, [...(groups.get(display_name) ?? []), value]);
	});

	return Array.from(groups, ([display_name, values]) => ({
		display_name,
		values,
	}));
});

const selectedDisplayNames = computed<string[]>({
	get: () =>
		groupedPossibleValues.value
			.filter((group) =>
				group.values.some(
					(value) => value !== null && selectedValues.value.includes(value)
				)
			)
			.map((group) => group.display_name),
	set: (displayNames) => {
		selectedValues.value = groupedPossibleValues.value
			.filter((group) => displayNames.includes(group.display_name))
			.flatMap((group) => group.values)
			.filter((value): value is string => value !== null);
	},
});

const loadPossibleValues = async () => {
	isLoadingPossibleValues.value = true;
	possibleValues.value = await useRuleEnginedStore().getDimensionValues(
		props.selectedRuleDimension.uuid
	);
	isLoadingPossibleValues.value = false;
};

watch(() => props.selectedRuleDimension.uuid, loadPossibleValues);

const hasPredefinedOptions = computed(
	() =>
		((props.type === "in_string" || props.type === "not_in_string") &&
			props.selectedRuleDimension.has_predefined_options) ||
		props.type === "day_of_week"
);

const isNumeric = computed(() =>
	["in_number", "not_in_number"].includes(props.type)
);

/** Matches the `numericAndFloat` validation rule used elsewhere. */
const NUMERIC_PATTERN = /^[+-]?([0-9]*[.])?[0-9]+$/;

const hasRejectedEntry = ref<boolean>(false);

/**
 * Freeform entries arrive from the combobox as strings, but `in_number` and
 * `not_in_number` are typed `number[]`. Anything non-numeric is refused rather
 * than stored, so the model never holds a value the backend cannot compare.
 */
const applyFreeformValues = (entered: (string | number)[]) => {
	if (!isNumeric.value) {
		hasRejectedEntry.value = false;
		selectedValues.value = entered;
		return;
	}

	const accepted: number[] = [];
	let rejected = false;

	entered.forEach((entry) => {
		if (typeof entry === "number") {
			accepted.push(entry);
			return;
		}

		if (NUMERIC_PATTERN.test(entry.trim())) {
			accepted.push(Number(entry));
		} else {
			rejected = true;
		}
	});

	hasRejectedEntry.value = rejected;
	selectedValues.value = accepted;
};

const displayNameFor = (value: string | number) =>
	possibleValues.value.find((option) => option.value === value)?.display_name ??
	String(value);
</script>

<template>
	<v-autocomplete
		v-if="hasPredefinedOptions"
		multiple
		chips
		closable-chips
		clearable
		variant="filled"
		density="compact"
		hide-details="auto"
		placeholder="Select values"
		item-title="display_name"
		item-value="display_name"
		:items="groupedPossibleValues"
		:loading="isLoadingPossibleValues"
		v-model="selectedDisplayNames"
	>
		<template v-slot:chip="{ props: chipProps, item }">
			<v-chip v-bind="chipProps" label size="small">
				{{ item.raw.display_name }}
			</v-chip>
		</template>
	</v-autocomplete>

	<v-combobox
		v-else
		multiple
		chips
		closable-chips
		clearable
		variant="filled"
		density="compact"
		hide-details="auto"
		:placeholder="
			isNumeric
				? 'Type a number and press Enter'
				: 'Type a value and press Enter'
		"
		:error-messages="hasRejectedEntry ? [t('validation.numericAndFloat')] : []"
		:model-value="selectedValues"
		@update:model-value="applyFreeformValues"
	>
		<template v-slot:chip="{ props: chipProps, item }">
			<v-chip v-bind="chipProps" label size="small">
				{{ displayNameFor(item.raw) }}
			</v-chip>
		</template>
	</v-combobox>
</template>
